import { FaLightbulb, FaBullseye, FaUsers, FaRocket, FaHandshake, FaChartLine } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import { Link } from 'react-router-dom';

const About = () => {
  const stats = [
    { label: "Active Learners", value: "10,000+" },
    { label: "Skills Tracked", value: "500+" },
    { label: "Career Paths", value: "120+" },
    { label: "Success Stories", value: "85%" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-7xl">
        
        {/* Header Section */}
        <div className="text-center mb-20 animate-fade-in-down">
          <div className="inline-flex items-center px-4 py-1.5 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold uppercase tracking-wider mb-6">
            <HiSparkles className="w-4 h-4 mr-2" />
            Our Story
          </div>
          
          <h1 className="heading-primary mb-6 text-gray-900">
            Bridging the Gap to Your <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Dream Career</span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Gaplify is more than just a platform; it's your personalized career compass. We empower students and professionals to navigate the complex landscape of modern skills and job markets.
          </p>
        </div>

        {/* Vision & Mission Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
            {/* Mission */}
            <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white text-2xl mb-6 shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform">
                    <FaBullseye />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed">
                    To democratize career growth by identifying skill gaps and providing actionable, data-driven pathways for every learner, ensuring no talent goes unrealized.
                </p>
            </div>

            {/* Vision */}
            <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group md:mt-8">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center text-white text-2xl mb-6 shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform">
                    <FaLightbulb />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed">
                    A world where career progression is transparent, accessible, and personalized, bridging the disconnect between education and industry requirements.
                </p>
            </div>

            {/* Values */}
            <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center text-white text-2xl mb-6 shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform">
                    <FaUsers />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Values</h3>
                <ul className="space-y-3 text-gray-600">
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div> Inclusivity & Accessibility</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div> Continuous Innovation</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div> Data-Driven Impact</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div> Learner-Centricity</li>
                </ul>
            </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="bg-gray-900 rounded-[3rem] overflow-hidden relative mb-20">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 to-blue-900 opacity-90"></div>
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
            
            <div className="relative z-10 px-8 py-20 text-center">
                <h2 className="heading-primary text-white mb-16">Why Choose Gaplify?</h2>
                
                <div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto">
                    <div className="text-center group">
                        <div className="w-20 h-20 mx-auto bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-3xl text-blue-300 mb-6 group-hover:bg-white/20 transition-all border border-white/10">
                            <FaRocket />
                        </div>
                         <h3 className="text-xl font-bold text-white mb-3">Personalized Roadmaps</h3>
                         <p className="text-blue-100/80">Tailored learning paths that adapt to your unique goals and current skill level.</p>
                    </div>

                    <div className="text-center group">
                        <div className="w-20 h-20 mx-auto bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-3xl text-purple-300 mb-6 group-hover:bg-white/20 transition-all border border-white/10">
                            <FaChartLine />
                        </div>
                         <h3 className="text-xl font-bold text-white mb-3">Real-Time Insights</h3>
                         <p className="text-blue-100/80">Market-driven data to ensure you're learning skills that employers actually hire for.</p>
                    </div>

                    <div className="text-center group">
                        <div className="w-20 h-20 mx-auto bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-3xl text-emerald-300 mb-6 group-hover:bg-white/20 transition-all border border-white/10">
                            <FaHandshake />
                        </div>
                         <h3 className="text-xl font-bold text-white mb-3">Community & Mentorship</h3>
                         <p className="text-blue-100/80">Connect with expert mentors and a vibrant community of peers to accelerate your growth.</p>
                    </div>
                </div>
            </div>
            
            {/* Stats Bar
            <div className="relative z-10 bg-white/5 backdrop-blur-lg border-t border-white/10 py-10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto text-center px-4">
                    {stats.map((stat, idx) => (
                        <div key={idx}>
                            <div className="text-3xl md:text-4xl font-black text-white mb-1">{stat.value}</div>
                            <div className="text-sm font-medium text-blue-200 uppercase tracking-wider">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </div> */}
        </div>

        {/* CTA Section */}
        <div className="text-center max-w-2xl mx-auto animate-fade-in-up">
            <h2 className="heading-secondary text-gray-900 mb-6">Ready to Start Your Journey?</h2>
            <p className="text-gray-600 mb-8 text-lg">Join thousands of learners who are already bridging the gap to their dream careers.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register" className="px-8 py-3.5 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-500/30 hover:bg-blue-700 hover:-translate-y-0.5 transition-all">
                    Get Started Free
                </Link>
                <Link to="/contact" className="px-8 py-3.5 bg-white text-gray-700 font-bold rounded-xl shadow-md border border-gray-200 hover:bg-gray-50 hover:text-gray-900 transition-all">
                    Contact Us
                </Link>
            </div>
        </div>

      </div>
    </div>
  );
};

export default About;